import React from "react";
import { useTranslation } from "react-i18next";
import useFormatCurrency from "../../../utils/useFormatCurrency";
import { ActionButton, Field, inputClass } from "../components/Lds";
import type { SchoolYearFormValues } from "../types";
import { useFeatureFlags } from "../../../features/featureFlags";

interface SchoolYearFormProps {
  values: SchoolYearFormValues;
  onChange: (values: SchoolYearFormValues) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
  showFee?: boolean;
}

const SchoolYearForm = ({ values, onChange, onSubmit, onCancel, submitting, submitLabel, showFee = false }: SchoolYearFormProps) => {
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();
  const { flags } = useFeatureFlags();

  const setValue = (name: keyof SchoolYearFormValues, value: string | number | null) => {
    onChange({ ...values, [name]: value });
  };

  return (
    <form onSubmit={onSubmit} className="rounded-[26px] border border-slate-200/80 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={t("years.common.start_date", { defaultValue: "Start date" })}>
          <input
            type="date"
            name="startDate"
            value={values.startDate}
            onChange={(event) => setValue("startDate", event.target.value)}
            className={inputClass}
            required
          />
        </Field>
        <Field label={t("years.common.end_date", { defaultValue: "End date" })}>
          <input
            type="date"
            name="endDate"
            value={values.endDate}
            onChange={(event) => setValue("endDate", event.target.value)}
            className={inputClass}
            required
          />
        </Field>
        {flags.schoolTasks && <>
        <Field label={t("years.common.self_signup_deadline", { defaultValue: "School tasks self-signup deadline" })}>
          <input
            type="date"
            name="selfSignupDeadline"
            value={values.selfSignupDeadline}
            onChange={(event) => setValue("selfSignupDeadline", event.target.value)}
            className={inputClass}
          />
        </Field>
        <p className="text-sm text-slate-600 dark:text-slate-400 sm:col-span-2">
          {t("years.common.self_signup_deadline_help", { defaultValue: "Parents choose their school tasks themselves until this date. Automatic assignment is possible from the day after. Leave empty for 1 October of the start year." })}
        </p>
        </>}
      </div>

      {showFee ? (
        <div className="mt-5">
          <Field label={t("years.common.default_fee", { defaultValue: "Default fee" })}>
            <input
              type="number"
              name="defaultMonthlyFee"
              step="0.01"
              value={values.defaultMonthlyFee ?? ""}
              onChange={(event) => setValue("defaultMonthlyFee", event.target.value === "" ? "" : Number(event.target.value))}
              className={inputClass}
              placeholder={t("years.edit.default_fee_placeholder", { defaultValue: "Default monthly fee" })}
              required
            />
          </Field>
          {values.defaultMonthlyFee !== "" && values.defaultMonthlyFee !== null ? (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              {t("years.edit.preview_fee", { defaultValue: "Preview fee" })}:{" "}
              <span className="font-mono">{formatCurrency(Number(values.defaultMonthlyFee))}</span>
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
        <ActionButton tone="secondary" onClick={onCancel}>
          {t("common.cancel", { defaultValue: "Cancel" })}
        </ActionButton>
        <ActionButton type="submit" disabled={submitting}>
          {submitting ? t("common.saving", { defaultValue: "Saving..." }) : submitLabel}
        </ActionButton>
      </div>
    </form>
  );
};

export default SchoolYearForm;
